/** Hover tooltip showing appointment details, anchored to a calendar event element. */

import {
  Building2,
  CalendarCheck,
  Clock,
  MessageCircleMore,
  NotebookPen,
  Stethoscope,
  User,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import {
  type AppointmentWithRelations,
  type DateFormat,
  formatDuration,
  formatName,
} from '@snootsvet/shared'

import { FormattedGridTimeRange } from '@/frontend/components/formatted-datetime'
import { calcHoverCardPosition } from '@/frontend/features/appointments/appointment-calendar.helpers'
import { AppointmentStatusBadge } from '@/frontend/features/appointments/appointment-status-badge'
import { getDisplayStatus } from '@/frontend/features/appointments/appointments.utils'
import { StripeNotLinkedBadge } from '@/frontend/features/billing/stripe-not-linked-badge'
import { SpeciesIcon } from '@/frontend/features/patient/species-icon'
import { usePatientAttributes } from '@/frontend/features/patient/use-patient-attributes'

interface AppointmentHoverCardProps {
  appointment: AppointmentWithRelations
  anchorEl: HTMLElement
  /**
   * The calendar's own frame. This card is anchored to a grid slot, so it reads in
   * the grid's zone and clock rather than the viewer's — otherwise its number
   * disagrees with the row it points at. The viewer's reading is annotated instead.
   */
  fmt: DateFormat
}

export function AppointmentHoverCard({ appointment, anchorEl, fmt }: AppointmentHoverCardProps) {
  const attributes = usePatientAttributes(appointment.patient, { shortAge: true })
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    const tooltip = tooltipRef.current
    if (!tooltip) return
    setPosition(calcHoverCardPosition(anchorEl, tooltip))
  }, [anchorEl])

  const start = new Date(appointment.startTime)
  const end = new Date(appointment.endTime)
  const durationMins = Math.round((end.getTime() - start.getTime()) / 60_000)
  const ownerName = `${appointment.patient.owner.firstName} ${appointment.patient.owner.lastName}`
  const vetName = formatName(appointment.staff)

  return createPortal(
    <div
      ref={tooltipRef}
      className="pointer-events-none fixed z-50 w-108 rounded-md border bg-popover px-3 py-2 shadow-md overflow-x-hidden animate-in fade-in-0 zoom-in-95"
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        // Invisible until positioned to avoid flash at -9999.
        opacity: position ? 1 : 0,
      }}
    >
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 font-semibold">
            <SpeciesIcon species={appointment.patient.species} className="size-4" />
            {appointment.patient.name}
            {!appointment.patient.stripeSubscriptionId && <StripeNotLinkedBadge />}
          </div>
          <AppointmentStatusBadge status={getDisplayStatus(appointment)} size="sm" />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-0.5 text-xs text-muted-foreground">
          {(['breed', 'age', 'sex', 'neuter'] as const).map((key) => {
            const { icon, label, value } = attributes[key]
            return (
              <span
                key={key}
                className="flex min-w-0 items-center gap-1 [&>svg]:size-3 [&>svg]:shrink-0"
              >
                {icon}
                <span className="sr-only">{label}: </span>
                <span className="min-w-0 break-words">{value}</span>
              </span>
            )
          })}
        </div>
        <div className="space-y-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Clock className="size-3 shrink-0" />
            <FormattedGridTimeRange start={start} end={end} fmt={fmt} /> (
            {formatDuration(durationMins)})
          </div>
          {appointment.appointmentTypeName && (
            <div className="flex items-center gap-1.5">
              <CalendarCheck className="size-3 shrink-0" />
              {appointment.appointmentTypeName}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <User className="size-3 shrink-0" />
            {ownerName}
            {!appointment.patient.owner.stripeCustomerId && <StripeNotLinkedBadge />}
          </div>
          {vetName && (
            <div className="flex items-center gap-1.5">
              <Stethoscope className="size-3 shrink-0" />
              {vetName}
            </div>
          )}
          {appointment.clinic && (
            <div className="flex items-center gap-1.5">
              <Building2 className="size-3 shrink-0" />
              {appointment.clinic.name}
            </div>
          )}
          {appointment.visitReason && (
            <div className="flex items-start gap-1.5">
              <NotebookPen className="size-3 shrink-0 mt-0.5" />
              <span className="line-clamp-5">{appointment.visitReason}</span>
            </div>
          )}
          {appointment.userNotes && (
            <div className="flex items-start gap-1.5">
              <MessageCircleMore className="size-3 shrink-0 mt-0.5" />
              <span className="line-clamp-5">{appointment.userNotes}</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
